import type { CSSProperties, ReactNode } from 'react';
import { Pin } from './Pin';

export type PostitTone = 'yellow' | 'pink';

export type PostitProps = {
  tone?: PostitTone;
  /** Rotation in degrees (default -3, slight tilt). */
  rot?: number;
  /** Width in px. */
  w?: number;
  /** Render a centred top pin head (default false). */
  withPin?: boolean;
  pinColor?: string;
  /** When true the post-it is a button (sets role + cursor). */
  onClick?: () => void;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
};

/**
 * Sticky post-it — yellow body by default, pink via `tone='pink'`.
 * Supplies the gradient + drop shadow + top-light highlight via the
 * `.postit` recipe. Use a `<HandKicker>` or short copy inside; never
 * stuff full paragraphs.
 */
export const Postit = ({
  tone = 'yellow',
  rot = -3,
  w = 130,
  withPin,
  pinColor,
  onClick,
  className,
  style,
  children,
}: PostitProps) => {
  const Cmp = onClick ? 'button' : 'div';
  const cls = ['postit', tone === 'pink' ? 'postit-pink' : '', className ?? '']
    .filter(Boolean)
    .join(' ');
  const finalStyle: CSSProperties = {
    width: w,
    padding: '10px 12px 12px',
    transform: `rotate(${rot}deg)`,
    ...(onClick ? { cursor: 'pointer', border: 'none' } : {}),
    ...(style ?? {}),
  };
  return (
    <Cmp className={cls} style={finalStyle} onClick={onClick} type={onClick ? 'button' : undefined}>
      {withPin && <Pin left={'50%'} top={-5} color={pinColor} />}
      {children}
    </Cmp>
  );
};
